/**
 * Fake Stock Counter Component
 * Displays a random stock count that decreases when user adds to cart
 */
class FakeStockCounter extends HTMLElement {
  constructor() {
    super();
    this.productId = this.dataset.productId;
    this.minStock = parseInt(this.dataset.minStock) || 50;
    this.maxStock = parseInt(this.dataset.maxStock) || 500;
    this.showProgressBar = this.dataset.showProgressBar === 'true';

    this.storageKey = `fake-stock-${this.productId}`;
    this.maxStorageKey = `fake-stock-max-${this.productId}`;
  }

  connectedCallback() {
    this.stockNumber = this.querySelector('[data-stock-number]');
    this.progressBar = this.querySelector('[data-progress-bar]');

    this.initStock();
    this.bindEvents();
  }

  disconnectedCallback() {
    this.unbindEvents();
  }

  initStock() {
    // Get stored stock or generate new one
    let currentStock = sessionStorage.getItem(this.storageKey);
    let maxStock = sessionStorage.getItem(this.maxStorageKey);

    // Regenerate if no stored value OR if stored value is outside current min/max range
    if (currentStock === null ||
      parseInt(currentStock) < this.minStock ||
      parseInt(currentStock) > this.maxStock) {
      // Generate random stock between min and max
      currentStock = Math.floor(Math.random() * (this.maxStock - this.minStock + 1)) + this.minStock;
      maxStock = currentStock;

      sessionStorage.setItem(this.storageKey, currentStock);
      sessionStorage.setItem(this.maxStorageKey, maxStock);
    }

    this.currentStock = parseInt(currentStock);
    this.initialMaxStock = parseInt(maxStock);

    this.updateDisplay();
  }

  bindEvents() {
    // Listen for cart add events
    this.onProductAdded = this.handleProductAdded.bind(this);
    document.addEventListener('product-ajax:added', this.onProductAdded);

    // Also listen to the MinimogEvents cart update
    if (window.MinimogEvents) {
      this.cartUpdateUnsubscriber = window.MinimogEvents.subscribe(
        window.MinimogTheme?.pubSubEvents?.cartUpdate,
        this.handleCartUpdate.bind(this)
      );
    }
  }

  unbindEvents() {
    document.removeEventListener('product-ajax:added', this.onProductAdded);
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  handleProductAdded(event) {
    const addedProduct = event.detail?.product;
    if (!addedProduct) return;

    // Check if this is the current product
    const addedProductId = addedProduct.product_id?.toString() || addedProduct.id?.toString();

    if (addedProductId === this.productId) {
      this.decrementStock();
    }
  }

  handleCartUpdate(event) {
    // This is triggered on cart updates, we can use it as a backup
    // The product-ajax:added event is more specific for our use case
  }

  decrementStock() {
    if (this.currentStock > 1) {
      this.currentStock -= 1;
      sessionStorage.setItem(this.storageKey, this.currentStock);
      this.updateDisplay();
    }
  }

  updateDisplay() {
    // Update the stock number
    if (this.stockNumber) {
      this.stockNumber.textContent = this.currentStock;
    }

    // Update progress bar - random percentage between 10% and 70% on load
    if (this.progressBar && this.showProgressBar) {
      // Check if we already have a stored progress percentage
      const progressKey = `fake-stock-progress-${this.productId}`;
      let progressPercentage = sessionStorage.getItem(progressKey);

      if (progressPercentage === null) {
        // Generate random percentage between 10% and 70%
        progressPercentage = Math.floor(Math.random() * (70 - 10 + 1)) + 10;
        sessionStorage.setItem(progressKey, progressPercentage);
      }

      this.progressBar.style.width = `${progressPercentage}%`;
    }

    // Add low stock class for visual urgency
    const lowThreshold = this.initialMaxStock * 0.2; // 20% or less is low
    if (this.currentStock <= lowThreshold) {
      this.classList.add('m-fake-stock--low');
    } else {
      this.classList.remove('m-fake-stock--low');
    }
  }
}

// Define the custom element
if (!customElements.get('fake-stock-counter')) {
  customElements.define('fake-stock-counter', FakeStockCounter);
}
